import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@/contexts';
import HomePage from './components/routes/home/HomePage';
import AboutPage from './components/routes/about/AboutPage';
import PrivacyNotice from './components/routes/privacy/PrivacyNotice';
import TermsOfService from './components/routes/terms/TermsOfService';
import { MainLayout } from './components/layouts';
import { ThemeToggle } from './components/ui/theme-toggle';
import { Button } from './components/ui/button';
import {
    Home,
    Info,
    FileText,
    Lock,
    Baby,
    BarChart3,
    Droplet
} from 'lucide-react';
import { MenuItem } from './components/layouts';

// Define main navigation menu - simplified
const navigationMenu: MenuItem[] = [
    {
        name: 'home',
        displayName: 'Home',
        path: '/',
        component: <HomePage />,
        icon: <Home className="h-4 w-4" />,
        navigate: 'header',
        order: 1
    },
    {
        name: 'about',
        displayName: 'About',
        path: '/about',
        component: <AboutPage />,
        icon: <Info className="h-4 w-4" />,
        navigate: 'header',
        order: 2
    },
    {
        name: 'products',
        displayName: 'Products',
        path: '/products',
        component: <div>
            <h1 className="text-2xl font-bold">Smart Diapers</h1>
            <p>Explore our range of smart diapers and analytics.</p>
        </div>,
        icon: <Baby className="h-4 w-4" />,
        navigate: 'header',
        order: 3,
        children: [
            {
                name: 'smartdiapers',
                displayName: 'Berry Sensor™',
                path: '/products/smart-diapers',
                component: <div>
                    <h1 className="text-2xl font-bold">Berry Sensor™</h1>
                    <p>Our revolutionary smart diaper technology.</p>
                </div>,
                icon: <Droplet className="h-4 w-4" />,
                navigate: 'header',
            },
            {
                name: 'analytics',
                displayName: 'Berry Analytics™',
                path: '/products/analytics',
                component: <div>
                    <h1 className="text-2xl font-bold">Berry Analytics™</h1>
                    <p>Monitor your baby's health data in real-time.</p>
                </div>,
                icon: <BarChart3 className="h-4 w-4" />,
                navigate: 'header',
            }
        ]
    },
    {
        name: 'terms',
        displayName: 'Terms of Service',
        path: '/terms',
        component: <TermsOfService />,
        icon: <FileText className="h-4 w-4" />,
        navigate: 'footer',
        order: 1
    },
    {
        name: 'privacy',
        displayName: 'Privacy Notice',
        path: '/privacy',
        component: <PrivacyNotice />,
        icon: <Lock className="h-4 w-4" />,
        navigate: 'footer',
        order: 2
    }
];

// Logo component with updated branding
const BerryBabiesLogo: React.FC = () => (
    <div className="flex items-center gap-3">
        <div className="p-2 bg-primary rounded-full text-white hover:scale-105 transition-transform duration-150">
            <img src="/logo.png" alt="Berry Babies Logo" className="h-8 w-8" />
        </div>
        <div className="flex flex-col">
            <span className="font-bold text-xl md:text-2xl text-header-foreground">
                Berry Babies
            </span>
            <span className="text-xs md:text-sm opacity-90 text-header-foreground">
                Your berry baby deserves the berry best
            </span>
        </div>
    </div>
);

// Additional header content
const HeaderExtraContent: React.FC = () => (
    <div className="flex items-center gap-1 sm:gap-2">
        <ThemeToggle />
        <Button size="sm" variant="outline" className="hidden md:flex btn-baby">
            Log In
        </Button>
        <Button size="sm" className="hidden sm:flex btn-baby">
            Try Berry Sensors
        </Button>
    </div>
);

// Footer extra content - mobile optimized
const FooterExtraContent: React.FC = () => (
    <div className="flex flex-col items-center md:items-end text-xs sm:text-sm max-w-[250px] sm:max-w-none">
        <div className="flex flex-wrap justify-center items-center gap-2">
            <div className="flex items-center gap-1">
                <Droplet className="h-3 w-3 text-dry" />
                <span>Dry</span>
            </div>
            <div className="flex items-center gap-1">
                <Droplet className="h-3 w-3 text-wet" />
                <span>Wet</span>
            </div>
            <div className="flex items-center gap-1">
                <Droplet className="h-3 w-3 text-change" />
                <span>Change</span>
            </div>
        </div>
        <span className="mt-2 opacity-80 text-center">Patent Pending Technology</span>
    </div>
);

const App: React.FC = () => {
    return (
        <ThemeProvider>
            <BrowserRouter>
                <MainLayout
                    menu={navigationMenu}
                    config={{
                        contentWidth: 'contained',
                        background: 'default',
                        logo: <BerryBabiesLogo />,
                        headerExtraContent: <HeaderExtraContent />,
                        footerExtraContent: <FooterExtraContent />,
                        containerClassName: "bg-pattern",
                        contentClassName: "py-8"
                    }}
                >
                    <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/privacy" element={<PrivacyNotice />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        {/* Additional routes would be added here */}
                    </Routes>
                </MainLayout>
            </BrowserRouter>
        </ThemeProvider>
    );
};

export default App;